import JSZip from 'jszip';
import { fetchGalleryDetailBySlug } from '../api/galleries';
import { downloadRemoteFile } from './downloadFile';

const PROBE_CONCURRENCY = 6;
const FETCH_CONCURRENCY = 6;

/**
 * @param {string} galleryTitle
 */
export function buildGalleryZipFilename(galleryTitle) {
    const base =
        String(galleryTitle || 'galeri')
            .trim()
            .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
            .replace(/\s+/g, '-')
            .slice(0, 64) || 'galeri';
    return base.toLowerCase().endsWith('.zip') ? base : `${base}.zip`;
}

/**
 * @param {string} url
 */
function guessExtensionFromUrl(url) {
    const path = String(url || '').split('?')[0].toLowerCase();
    const match = path.match(/\.(jpe?g|png|gif|webp|bmp|avif|heic)$/i);
    return match ? `.${match[1].toLowerCase().replace('jpeg', 'jpg')}` : '.jpg';
}

/**
 * @param {string} galleryTitle
 * @param {number} index
 * @param {string} url
 */
export function buildGalleryImageFilename(galleryTitle, index, url) {
    const base =
        String(galleryTitle || 'galeri')
            .trim()
            .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
            .replace(/\s+/g, '-')
            .slice(0, 48) || 'galeri';
    const ext = guessExtensionFromUrl(url);
    return `${base}-${String(index + 1).padStart(2, '0')}${ext}`;
}

/**
 * @param {string} url
 * @param {string | null | undefined} accessToken
 */
async function probeRemoteFileSize(url, accessToken) {
    const headers = /** @type {Record<string, string>} */ ({});
    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }

    try {
        const headRes = await fetch(url, { method: 'HEAD', headers });
        if (headRes.ok) {
            const cl = headRes.headers.get('Content-Length');
            const parsed = cl ? parseInt(cl, 10) : 0;
            if (Number.isFinite(parsed) && parsed > 0) {
                return parsed;
            }
        }
    } catch {
        /* HEAD başarısız — GET ile dene */
    }

    try {
        const res = await fetch(url, { headers });
        if (!res.ok) return 0;
        const blob = await res.blob();
        return blob.size;
    } catch {
        return 0;
    }
}

/**
 * @param {string[]} urls
 * @param {string | null | undefined} accessToken
 */
export async function probeGalleryTotalBytes(urls, accessToken) {
    const list = urls.filter(Boolean);
    if (list.length === 0) return 0;

    /** @type {number[]} */
    const sizes = new Array(list.length).fill(0);
    let cursor = 0;

    async function worker() {
        for (;;) {
            const i = cursor;
            cursor += 1;
            if (i >= list.length) break;
            sizes[i] = await probeRemoteFileSize(list[i], accessToken);
        }
    }

    const workers = Array.from(
        { length: Math.min(PROBE_CONCURRENCY, list.length) },
        () => worker(),
    );
    await Promise.all(workers);
    return sizes.reduce((sum, n) => sum + (Number.isFinite(n) ? n : 0), 0);
}

/**
 * @param {string} accessToken
 * @param {import('../data/galleries').GalleryAlbum} gallery
 */
export async function fetchGalleryImagesForDownload(accessToken, gallery) {
    const slug = String(gallery.slug || gallery.id || '').trim();
    if (!slug) {
        throw new Error('Galeri kimliği bulunamadı');
    }

    const { payload } = await fetchGalleryDetailBySlug(accessToken, slug);
    return payload.images.map((img) => ({
        id: img.id,
        src: img.src,
        alt: img.alt,
    }));
}

/**
 * @param {string} url
 * @param {string | null | undefined} accessToken
 */
async function fetchImageBlob(url, accessToken) {
    const headers = /** @type {Record<string, string>} */ ({});
    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }

    const res = await fetch(url, { headers });
    if (!res.ok) {
        throw new Error(`Görsel indirilemedi (${res.status})`);
    }
    return res.blob();
}

/**
 * Galeri görsellerini tek bir .zip dosyası olarak indirir.
 * @param {Array<{ src: string }>} images
 * @param {string} galleryTitle
 * @param {string | null | undefined} accessToken
 * @param {{ onProgress?: (done: number, total: number) => void, onZipProgress?: (percent: number) => void }} [opts]
 */
export async function downloadGalleryImagesAsZip(
    images,
    galleryTitle,
    accessToken,
    opts = {},
) {
    const list = images.filter((img) => img?.src?.trim());
    if (list.length === 0) {
        throw new Error('İndirilecek görsel yok');
    }

    const zip = new JSZip();
    let cursor = 0;

    async function worker() {
        for (;;) {
            const i = cursor;
            cursor += 1;
            if (i >= list.length) break;

            const img = list[i];
            const blob = await fetchImageBlob(img.src, accessToken);
            zip.file(
                buildGalleryImageFilename(galleryTitle, i, img.src),
                blob,
            );
            opts.onProgress?.(i + 1, list.length);
        }
    }

    const workers = Array.from(
        { length: Math.min(FETCH_CONCURRENCY, list.length) },
        () => worker(),
    );
    await Promise.all(workers);

    const zipBlob = await zip.generateAsync(
        {
            type: 'blob',
            // JPEG/PNG zaten sıkıştırılmış; STORE çok daha hızlı, boyut neredeyse aynı.
            compression: 'STORE',
        },
        (metadata) => {
            opts.onZipProgress?.(metadata.percent);
        },
    );

    const objectUrl = URL.createObjectURL(zipBlob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = buildGalleryZipFilename(galleryTitle);
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Tek bir galeri görselini indirir.
 * @param {string} src
 * @param {string} galleryTitle
 * @param {number} index
 * @param {string | null | undefined} accessToken
 */
export async function downloadSingleGalleryImage(
    src,
    galleryTitle,
    index,
    accessToken,
) {
    await downloadRemoteFile(
        src,
        buildGalleryImageFilename(galleryTitle, index, src),
        { accessToken },
    );
}
